import { Search, StethoscopeIcon, UserPlusIcon, X } from "lucide-react";
import { useCallback, useDeferredValue, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	EMPLOYMENT_TYPES,
	PREFERRED_THERAPIST_GENDER_LABELS,
} from "@/lib/constants";
import { cn } from "@/lib/utils";

interface Therapist {
	id: string;
	name: string;
	email?: string;
	registrationNumber: string;
	employmentType: string;
	gender: string;
	activeAddress?: {
		location?: {
			city: string;
		};
	};
}

interface BulkActionButtonsProps {
	onApplyTherapistToAll: (therapist: Therapist | null) => void;
}

export function BulkActionButtons({
	onApplyTherapistToAll,
}: BulkActionButtonsProps) {
	const { t } = useTranslation("appointments-form");
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [therapists, setTherapists] = useState<Therapist[]>([]);
	const [selectedTherapist, setSelectedTherapist] = useState<Therapist | null>(
		null,
	);
	const [employmentType, setEmploymentType] = useState<
		"ALL" | "KARPIS" | "FLAT"
	>("ALL");
	const [preferredTherapistGender, setPreferredTherapistGender] = useState<
		"NO PREFERENCE" | "MALE" | "FEMALE"
	>("NO PREFERENCE");
	const deferredSearchQuery = useDeferredValue(searchQuery);
	const handleResetFilters = useCallback(() => {
		setSearchQuery("");
		setEmploymentType("ALL");
		setPreferredTherapistGender("NO PREFERENCE");
	}, []);

	const fetchTherapists = useCallback(async () => {
		const params = new URLSearchParams({
			search_query: deferredSearchQuery,
			employment_type: employmentType,
			is_all_of_day: "false",
			preferred_therapist_gender: preferredTherapistGender,
			location_id: "",
			service_id: "",
			appointment_date_time: "",
			appointment_id: "",
		});

		try {
			const response = await fetch(`/api/v1/therapists/search?${params}`);
			const data = await response.json();
			if (data.therapists) {
				setTherapists(data.therapists);
			}
		} catch (err) {
			console.error("Failed to fetch therapists:", err);
		}
	}, [deferredSearchQuery, employmentType, preferredTherapistGender]);

	const handleSelectTherapist = (therapist: Therapist) => {
		setSelectedTherapist(therapist);
	};

	const handleApplyTherapist = () => {
		if (selectedTherapist) {
			onApplyTherapistToAll(selectedTherapist);
		}
		setIsOpen(false);
		setTherapists([]);
		setSelectedTherapist(null);
		handleResetFilters();
	};

	// Fetch therapists with debounced search query
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (deferredSearchQuery || therapists.length === 0) {
				fetchTherapists();
			}
		}, 300);

		return () => clearTimeout(timeoutId);
	}, [deferredSearchQuery, fetchTherapists, therapists.length]);

	// Fetch therapists when filters change
	useEffect(() => {
		fetchTherapists();
	}, [fetchTherapists]);

	// Reset search query and therapists when drawer opens
	useEffect(() => {
		if (!isOpen) {
			setSelectedTherapist(null);
			setTherapists([]);
			handleResetFilters();
		}
	}, [isOpen, handleResetFilters]);

	const groupedTherapists = therapists.reduce(
		(groups, therapist) => {
			const city =
				therapist.activeAddress?.location?.city || t("common.not_applicable");
			if (!groups[city]) {
				groups[city] = [];
			}
			groups[city].push(therapist);
			return groups;
		},
		{} as Record<string, Therapist[]>,
	);

	return (
		<div className="flex items-center gap-2">
			<Drawer open={isOpen} onOpenChange={setIsOpen}>
				<Button
					type="button"
					variant="accent-outline"
					className="shadow-none"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setIsOpen(true);
					}}
				>
					<UserPlusIcon className="h-4 w-4" />
					{t(
						"bulk_reschedule.actions.apply_therapist",
						"Assign Therapist to All",
					)}
				</Button>

				<DrawerContent className="max-h-screen">
					<div className="mx-auto w-full max-w-sm">
						<DrawerHeader>
							<DrawerTitle>
								{t(
									"bulk_reschedule.row.select_therapist",
									"Select Therapist for All Visit",
								)}
							</DrawerTitle>
						</DrawerHeader>

						<div className="no-scrollbar overflow-y-auto p-4 max-h-[80dvh] md:max-h-[88dvh] space-y-6">
							<div className="space-y-4">
								<div className="space-y-2">
									<Label>
										{t(
											"unassigned_visits.reblast.channel_label",
											"Employment Type",
										)}
									</Label>

									<div className="flex flex-wrap gap-2 items-center">
										{[...EMPLOYMENT_TYPES, "ALL" as const].map((type) => (
											<Button
												key={type}
												type="button"
												size="sm"
												onClick={() => setEmploymentType(type)}
												className={cn(
													"uppercase font-bold tracking-wider transition-colors border shadow-none",
													employmentType === type
														? "bg-primary text-primary-foreground border-primary"
														: "bg-input/50 text-input-foreground border-border hover:bg-accent hover:border-border hover:text-accent-foreground",
												)}
											>
												{t(`common.employment_types.${type}`, type)}
											</Button>
										))}
									</div>
								</div>

								<div className="space-y-2">
									<Label>
										{t(
											"appt_schedule.fields.pref_therapist_gender.label",
											"Preferred Gender",
										)}
									</Label>

									<div className="flex flex-wrap gap-2">
										{PREFERRED_THERAPIST_GENDER_LABELS.map((gender) => (
											<Button
												key={gender.title}
												type="button"
												size="sm"
												onClick={() =>
													setPreferredTherapistGender(gender.title)
												}
												className={cn(
													"uppercase font-bold tracking-wider transition-colors border shadow-none",
													preferredTherapistGender === gender.title
														? "bg-primary text-primary-foreground border-primary"
														: "bg-input/50 text-input-foreground border-border hover:bg-accent hover:border-border hover:text-accent-foreground",
												)}
											>
												{t(
													`appt_schedule.fields.pref_therapist_gender.options.${gender.title.toLowerCase()}`,
													gender.title,
												)}
											</Button>
										))}
									</div>
								</div>

								<div className="space-y-2 !mt-6">
									<Label htmlFor="bulk-therapist-search" className="sr-only">
										{t(
											"therapists.filters.search_placeholder",
											"Search Therapist",
										)}
									</Label>
									<div className="relative">
										<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
										<Input
											id="bulk-therapist-search"
											placeholder={t(
												"therapists.filters.search_placeholder",
												"Search by name, registration number or email...",
											)}
											className="pl-9 pr-9 bg-input/50"
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
										/>
										{searchQuery && (
											<button
												type="button"
												className="absolute right-2 top-2.5"
												onClick={(e) => {
													e.stopPropagation();
													e.preventDefault();
													setSearchQuery("");
													setTherapists([]);
												}}
											>
												<X className="h-4 w-4 text-muted-foreground" />
											</button>
										)}
									</div>
								</div>
							</div>

							<div className="space-y-4">
								{/* Therapists count across multiple cities */}
								<div className="w-full">
									<span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground/75 flex items-center gap-2">
										{therapists.length > 0
											? t("therapists.filters.found_count", {
													count: therapists.length,
													cityCount: Object.keys(groupedTherapists).length,
													defaultValue: `${therapists.length} therapists found across ${Object.keys(groupedTherapists).length} cities`,
												})
											: t(
													"therapists.filters.search_instruction",
													"Search for therapists",
												)}
										<span className="h-[2px] flex-1 bg-border" />
									</span>
								</div>

								<div className="max-h-[250px] md:max-h-[700px] overflow-y-auto border rounded-md bg-muted">
									{/* No therapists found message */}
									{therapists.length === 0 && !searchQuery && (
										<div className="text-center text-sm text-muted-foreground/75 p-4 italic">
											<p>
												{t(
													"therapists.filters.search_instruction",
													"Start typing to search therapists",
												)}
											</p>
										</div>
									)}

									{/* No therapists found with search query */}
									{therapists.length === 0 && searchQuery && (
										<div className="text-center text-sm text-muted-foreground/75 p-4 italic">
											<p>
												{t("therapists.filters.not_found", {
													query: searchQuery,
													defaultValue: `No therapist found for "${searchQuery}"`,
												})}
											</p>
										</div>
									)}

									{/* Therapists list */}
									{therapists?.length > 0 &&
										Object.entries(groupedTherapists).map(
											([city, cityTherapists]) => (
												<div key={city}>
													<div className="px-4 py-2 bg-muted text-xs font-medium text-muted-foreground sticky top-0 z-10 border-b border-r">
														<span>{city}</span>
													</div>

													<div className="grid">
														{cityTherapists.map((therapist) => (
															<button
																key={therapist.id}
																onClick={(e) => {
																	e.preventDefault();
																	e.stopPropagation();
																	handleSelectTherapist(therapist);
																}}
																type="button"
																className={cn(
																	"w-full text-left px-4 py-3 bg-card hover:bg-accent transition-colors flex items-center gap-3 border-b",
																	selectedTherapist?.id === therapist.id
																		? "bg-primary/5 text-primary"
																		: "",
																)}
															>
																<div
																	className={cn(
																		"w-10 h-10 border rounded-full flex items-center justify-center flex-shrink-0",
																		selectedTherapist?.id === therapist.id
																			? "bg-primary text-primary-foreground border-primary"
																			: "bg-muted text-muted-foreground border-border",
																	)}
																>
																	<StethoscopeIcon className="w-5 h-5 shrink-0" />
																</div>

																<div className="flex-1 min-w-0">
																	<p className="font-medium text-sm uppercase tracking-tight truncate">
																		{therapist.name}
																	</p>
																	{therapist.email && (
																		<p className="text-xs truncate opacity-75">
																			{therapist.email}
																		</p>
																	)}
																	<p
																		className={cn(
																			"text-xs opacity-75",
																			selectedTherapist?.id === therapist.id
																				? "text-primary"
																				: "text-muted-foreground",
																		)}
																	>
																		<span>
																			{therapist.employmentType
																				? t(
																						`common.employment_types.${therapist.employmentType}`,
																						therapist.employmentType,
																					)
																				: t("common.not_applicable", "N/A")}
																		</span>
																		<span className="mx-1">&bull;</span>
																		<span>
																			{therapist.gender
																				? t(
																						`appt_schedule.fields.pref_therapist_gender.options.${therapist.gender.toLowerCase()}`,
																						therapist.gender,
																					)
																				: t("common.not_applicable", "N/A")}
																		</span>
																	</p>
																</div>
															</button>
														))}
													</div>
												</div>
											),
										)}
								</div>
							</div>

							{selectedTherapist && (
								<div className="space-y-4">
									{/* Therapists count across multiple cities */}
									<div className="w-full">
										<span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground/75 flex items-center gap-2">
											<span>
												{t(
													"bulk_reschedule.row.new_therapist",
													"Selected Therapist",
												)}
											</span>
											<span className="h-[2px] flex-1 bg-border" />
										</span>
									</div>

									<div className="flex items-center gap-3 px-4 py-3 border rounded-md bg-card">
										<div className="w-10 h-10 bg-muted text-muted-foreground border border-border rounded-full flex items-center justify-center flex-shrink-0">
											<StethoscopeIcon className="w-5 h-5 shrink-0" />
										</div>

										<div className="flex-1 min-w-0">
											<p className="font-medium text-sm uppercase tracking-tight truncate">
												{selectedTherapist.name}
											</p>
											<p className="text-xs text-muted-foreground opacity-75">
												<span>
													{selectedTherapist.employmentType
														? t(
																`common.employment_types.${selectedTherapist.employmentType}`,
																selectedTherapist.employmentType,
															)
														: t("common.not_applicable", "N/A")}
												</span>
												<span className="mx-1">&bull;</span>
												<span>
													{selectedTherapist.gender
														? t(
																`appt_schedule.fields.pref_therapist_gender.options.${selectedTherapist.gender.toLowerCase()}`,
																selectedTherapist.gender,
															)
														: t("common.not_applicable", "N/A")}
												</span>
											</p>
										</div>

										<button
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												e.preventDefault();
												setSelectedTherapist(null);
											}}
											className="p-1 hover:bg-accent rounded-full"
										>
											<X className="h-4 w-4 text-muted-foreground" />
										</button>
									</div>
								</div>
							)}
						</div>

						<DrawerFooter className="sticky bottom-0 gap-4 md:gap-2 flex-col md:flex-row">
							<Button
								type="button"
								className="w-full sm:flex-1"
								disabled={!selectedTherapist}
								onClick={handleApplyTherapist}
							>
								{t("button.save.label", "Apply")}
							</Button>

							<DrawerClose asChild>
								<Button
									type="button"
									variant="outline"
									className="w-full sm:w-auto"
								>
									{t("button.cancel", "Cancel")}
								</Button>
							</DrawerClose>
						</DrawerFooter>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	);
}
